import { Grid, Column, DEFAULT_DYNAMIC_GRID, DEFAULT_DYNAMIC_GRID_PLACEHOLDER } from '@packages/models/api';

export const DYNAMIC_GRID_TEMPLATE_TYPES = {
    custom: { label: 'Custom Table' },
    tireInflationPressure: { label: 'Tire Inflation Pressure' },
    wheelBalance: { label: 'Wheel Balance' },
    roadForceBalancing: { label: 'Road Force Balancing' },
    compressionAndLeakDownTest: { label: 'Compression and Leak Down Test' },
} as const;

export type DynamicGridTemplateType = keyof typeof DYNAMIC_GRID_TEMPLATE_TYPES;

type ColumnDefaults = {
    defaultValue?: string;
    isRequired?: boolean;
};

function createColumn(columnLabel: string, columnOrder: number, defaults?: ColumnDefaults): Column {
    return {
        worksheetElementGridId: '',
        columnLabel,
        columnOrder,
        defaultValue: defaults?.defaultValue ?? '',
        isRequired: defaults?.isRequired ?? false,
        subColumns: [],
    };
}

function createColumns(columnLabels: string[], defaults?: ColumnDefaults): Column[] {
    return columnLabels.map((label, index) => createColumn(label, index + 1, defaults));
}

function createRow(rowLabel: string, rowOrder: number, columns: Column[]): Grid {
    return {
        rowLabel,
        rowOrder,
        subRows: [],
        columns,
    };
}

function createGrid(rowLabels: string[], createColumnsFn: () => Column[]): Grid[] {
    return rowLabels.map((rowLabel, index) => createRow(rowLabel, index + 1, createColumnsFn()));
}

const DEFAULT_POSITIONS = ['Left Front', 'Left Rear', 'Right Front', 'Right Rear'] as const;

export const DYNAMIC_GRID_TEMPLATES: Record<DynamicGridTemplateType, { formElementPlaceholder: string; grid: Grid[] }> =
    {
        custom: {
            formElementPlaceholder: DEFAULT_DYNAMIC_GRID_PLACEHOLDER,
            grid: DEFAULT_DYNAMIC_GRID,
        },

        tireInflationPressure: {
            formElementPlaceholder: 'Position',
            grid: createGrid([...DEFAULT_POSITIONS], () =>
                createColumns(['Before (PSI)', 'After (PSI)'], { isRequired: true })
            ),
        },

        wheelBalance: {
            formElementPlaceholder: 'Position',
            grid: createGrid([...DEFAULT_POSITIONS], () =>
                createColumns(['Pre-Inside', 'Pre-Outside', 'Post-Inside', 'Post-Outside', 'Residual'], {
                    isRequired: true,
                })
            ),
        },

        roadForceBalancing: {
            formElementPlaceholder: 'Position',
            grid: createGrid([...DEFAULT_POSITIONS], () => [
                ...createColumns(['R1H (kg)', 'R2H (kg)', 'R3H (kg)'], { isRequired: true }),
                createColumn('Total TIR (mm)', 4, { isRequired: false }),
            ]),
        },

        compressionAndLeakDownTest: {
            formElementPlaceholder: 'Cylinder',
            grid: [
                createRow('Cylinder leak down (%)', 1, createColumns(['1', '2', '3', '4', '5', '6'])),
                createRow('Leakage (I / E / C / N)', 2, createColumns(['1', '2', '3', '4', '5', '6'])),
                createRow('Compression Test Dry (psi)', 3, createColumns(['1', '2', '3', '4', '5', '6'])),
                createRow('Compression Test Wet (psi)', 4, createColumns(['1', '2', '3', '4', '5', '6'])),
            ],
        },
    };
